import transparentBackground from "@/assets/images/transparent.jpg";
import {
  GenerateWordAudioDocument,
  TextsToSpeechesDocument,
  UpdateSlideDocument,
} from "@/generated/graphql";
import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { apolloClient } from "@/lib/graphql-client";
import { useWordHandlers } from "@/lib/hooks/slides/editor/handlers/word/useWordHandlers";
import { useLocalAudioUpload } from "@/lib/hooks/slides/editor/useLocalAudioUpload";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import { withToast } from "@/lib/utils/withToast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import { MediaUsageContextSchema, type WordSlideData } from "@ai-teach/types";
import { Button } from "@ai-teach/ui-web";
import { AZURE_VOICES, DEFAULT_VOICE } from "@ai-teach/utils";
import { useMutation } from "@apollo/client/react";
import { type MouseEvent, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { AiFillPicture } from "react-icons/ai";
import { FaChevronRight, FaMagic, FaMusic, FaPause, FaPlay } from "react-icons/fa";
import { HiX } from "react-icons/hi";
import { useSlideEditorContext } from "../../../SlideEditorContext";
import { EditorContainer } from "../../EditorContainer";

// Module-level cache so each voice's sample is only generated once per session.
const voicePreviewCache = new Map<string, string>();

/** Pause between sound clips, matching the phone. */
const WEB_INTER_PHONEME_GAP_MS = 850;

export function WordPronunciationEditor() {
  const [playingIndex, setPlayingIndex] = useState<number | "word" | "sentence" | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [durations, setDurations] = useState<{ word?: number; sentence?: number }>({});
  const [voiceOverride, setVoiceOverride] = useState<string | null>(null);
  const ttsVoice = voiceOverride ?? DEFAULT_VOICE;
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const wordHandlers = useWordHandlers(slide ?? null);
  const { handleFileDrop: handleImageFileDrop, isUploading: isUploadingImage } =
    useLocalImageUpload((media) => wordHandlers.addImage?.(media));
  const { handleFileDrop: handleAudioFileDrop } = useLocalAudioUpload((media) =>
    wordHandlers.addAudio?.(media)
  );

  const wordSlideData = slide?.slideData as WordSlideData | undefined;

  if (!slide || !wordSlideData) {
    return null;
  }

  const {
    word,
    sentence,
    image,
    syllable,
    wordAudio,
    sentenceAudio,
    syllableAudios,
    isVideoWithImage,
    recordOnly,
  } = wordSlideData;
  const slideName = slide?.name || "";

  const { setActivePanel } = useSlideEditorContext();

  const [generateWordAudioMutation] = useMutation(GenerateWordAudioDocument);
  const [textsToSpeechesMutation] = useMutation(TextsToSpeechesDocument);
  const [busy, setBusy] = useState<"word" | "sentence" | "all" | null>(null);
  const wordBusy = busy === "word" || busy === "all";
  const sentenceBusy = busy === "sentence" || busy === "all";
  const anyBusy = busy !== null;
  const [showSyllables, setShowSyllables] = useState(true);
  const slideId = slide.id;
  // Re-open the syllable breakdown on every slide, even if it was collapsed on another.
  useEffect(() => {
    if (slideId) setShowSyllables(true);
  }, [slideId]);

  const audioRefs = useRef<(HTMLAudioElement | null)[]>([]);
  const wordAudioRef = useRef<HTMLAudioElement | null>(null);
  const sentenceAudioRef = useRef<HTMLAudioElement | null>(null);
  // One shared element for syllable playback; multi-sound clips chain through syllableQueueRef on `onended`.
  const syllableAudioRef = useRef<HTMLAudioElement | null>(null);
  const syllableQueueRef = useRef<string[]>([]);
  const gapTimerRef = useRef<number | null>(null);

  const stopAllAudio = () => {
    if (gapTimerRef.current !== null) {
      window.clearTimeout(gapTimerRef.current);
      gapTimerRef.current = null;
    }
    for (const audio of audioRefs.current) {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    }
    for (const ref of [wordAudioRef.current, sentenceAudioRef.current, syllableAudioRef.current]) {
      if (ref) {
        ref.pause();
        ref.currentTime = 0;
      }
    }
    syllableQueueRef.current = [];
  };

  /** The ordered clip(s) to play for one syllable: its sound units in sequence, else its single `audioUrl`. */
  const soundUnitUrls = (item: (typeof syllableAudios)[number]): string[] =>
    item.soundUnits && item.soundUnits.length > 0
      ? item.soundUnits.map((u) => u.audioUrl)
      : [item.audioUrl];

  const handleSyllablePlay = (index: number) => {
    const item = syllableAudios[index];
    const audio = syllableAudioRef.current;
    if (!item || !audio) return;

    if (playingIndex === index) {
      stopAllAudio();
      audio.pause();
      audio.currentTime = 0;
      setPlayingIndex(null);
      return;
    }

    stopAllAudio();
    const [first, ...rest] = soundUnitUrls(item);
    if (!first) return;
    syllableQueueRef.current = rest;
    audio.src = first;
    audio.currentTime = 0;
    audio.play();
    setPlayingIndex(index);
  };

  const handleSyllableEnded = () => {
    const next = syllableQueueRef.current.shift();
    const audio = syllableAudioRef.current;
    if (next && audio) {
      gapTimerRef.current = window.setTimeout(() => {
        gapTimerRef.current = null;
        audio.src = next;
        audio.play();
      }, WEB_INTER_PHONEME_GAP_MS);
    } else {
      setPlayingIndex(null);
    }
  };

  const clipRef = (index: number | "word" | "sentence") =>
    index === "word"
      ? wordAudioRef.current
      : index === "sentence"
        ? sentenceAudioRef.current
        : audioRefs.current[index];

  const formatTime = (t: number) => {
    if (!Number.isFinite(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleAudioPlay = (index: number | "word" | "sentence") => {
    const currentAudio = clipRef(index);
    if (!currentAudio) return;

    if (playingIndex === index) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setPlayingIndex(null);
      setCurrentTime(0);
      return;
    }

    stopAllAudio();
    setCurrentTime(0);
    currentAudio.currentTime = 0;
    currentAudio.play();
    setPlayingIndex(index);
    currentAudio.ontimeupdate = () => setCurrentTime(currentAudio.currentTime);
    currentAudio.onended = () => {
      setPlayingIndex(null);
      setCurrentTime(0);
    };
  };

  const handleSeek = (kind: "word" | "sentence", e: MouseEvent<HTMLElement>) => {
    const audio = clipRef(kind);
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const target = ratio * audio.duration;
    if (playingIndex !== kind) handleAudioPlay(kind);
    audio.currentTime = target;
    setCurrentTime(target);
  };

  // Persists to the server on completion, so leaving mid-generation doesn't lose the result.
  const runGenerateAll = async (withSentence = true) => {
    if (!slide) return;
    const slideId = slide.id;
    const baseSlideData = wordSlideData; // snapshot at click time — closure keeps it after navigation
    const trimmedWord = (word ?? "").trim();
    if (trimmedWord === "") {
      toast.error("Enter a word first");
      return;
    }
    const trimmedSentence = withSentence ? (sentence?.trim() ?? "") : "";
    setBusy(withSentence ? "all" : "word");

    const speechArgs = (text: string) => ({
      variables: {
        texts: [text],
        voice: ttsVoice,
        slideId,
        usageContext: MediaUsageContextSchema.Values.aiTextToSpeech,
        speed: 0.8,
        pitch: 0,
      },
    });

    await withToast(
      async () => {
        const [wordSpeech, syllableRes, sentenceSpeech] = await Promise.all([
          textsToSpeechesMutation(speechArgs(trimmedWord)),
          generateWordAudioMutation({
            variables: { word: trimmedWord, voice: ttsVoice, slideId },
          }),
          trimmedSentence
            ? textsToSpeechesMutation(speechArgs(trimmedSentence))
            : Promise.resolve(null),
        ]);

        const wordClip = wordSpeech.data?.textsToSpeeches?.[0];
        const syllables = syllableRes.data?.generateWordAudio;
        if (!wordClip || !syllables) throw new Error();

        const sentenceClip = sentenceSpeech?.data?.textsToSpeeches?.[0];

        const nextSlideData = {
          ...baseSlideData,
          wordAudio: {
            src: wordClip.src ?? "",
            filename: wordClip.filename ?? "",
            id: wordClip.id ?? "",
          },
          syllable: syllables[0]?.syllableBreakdown ?? syllables.map((s) => s.syllable).join("-"),
          syllableAudios: syllables.map((s) => ({
            syllable: s.syllable,
            audioUrl: s.audioUrl,
            audioDuration: String((s.audioDuration / 10 ** 7).toFixed(2)),
            audioId: s.audioId,
            // Must be carried through, not dropped, or this save overwrites the server's sound breakdown.
            ...(s.soundUnits && s.soundUnits.length > 0
              ? {
                  soundUnits: s.soundUnits.map((u) => ({
                    grapheme: u.grapheme,
                    ipa: u.ipa,
                    phonemeId: u.phonemeId ?? null,
                    audioUrl: u.audioUrl,
                    audioDuration: String((u.audioDuration / 10 ** 7).toFixed(2)),
                    audioId: u.audioId,
                    source: u.source as "phonics" | "azure",
                  })),
                }
              : {}),
          })),
          ...(sentenceClip
            ? {
                sentenceAudio: {
                  src: sentenceClip.src ?? "",
                  filename: sentenceClip.filename ?? "",
                  id: sentenceClip.id ?? "",
                },
              }
            : {}),
        };

        await apolloClient.mutate({
          mutation: UpdateSlideDocument,
          variables: { input: { slideId, slideData: nextSlideData } },
        });
        // Reflect it in the editor when it's still open on this slide (the reducer no-ops otherwise).
        dispatch(updateSlide({ slideId, slideData: nextSlideData }));
      },
      {
        loading: "Generating audio…",
        success: "Audio generated & saved",
        error: "Failed to generate audio",
      },
      "generateWordAllToast"
    )
      .catch(() => {})
      .finally(() => setBusy(null));
  };

  const handleGenerateAllClick = () => runGenerateAll(true);
  const handleRegenerateWord = () => runGenerateAll(false);

  const handleRemoveMedia = (type: "wordAudio" | "image" | "sentenceAudio") => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...wordSlideData,
          [type]: { id: null, src: null, filename: null },
        },
      })
    );
  };

  // Play a fixed sample sentence with the selected voice; results are cached per voice.
  const handlePreviewVoice = async () => {
    if (!slide) return;

    setPreviewLoading(true);
    try {
      let src = voicePreviewCache.get(ttsVoice);
      if (!src) {
        const voiceLabel = AZURE_VOICES.find((v) => v.value === ttsVoice)?.label ?? "Andrew";
        const res = await textsToSpeechesMutation({
          variables: {
            texts: [`Hello, how are you? My name is ${voiceLabel}.`],
            voice: ttsVoice,
            slideId: slide.id,
            usageContext: MediaUsageContextSchema.Values.aiTextToSpeech,
            speed: 0.8,
            pitch: 0,
          },
        });
        src = res.data?.textsToSpeeches?.[0]?.src ?? undefined;
        if (!src) throw new Error();
        voicePreviewCache.set(ttsVoice, src);
      }

      previewAudioRef.current?.pause();
      const audio = new Audio(src);
      previewAudioRef.current = audio;
      await audio.play();
    } catch (error) {
      if (!toastAiCredits(error, "voicePreviewToast")) toast.error("Failed to preview audio");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerateSentenceAudio = () => {
    if (!slide) return;

    const slideId = slide.id;
    const baseSlideData = wordSlideData;
    const text = sentence?.trim() ?? "";
    if (text === "") {
      toast.error("Enter a sentence first to generate audio");
      return;
    }

    setBusy("sentence");
    withToast(
      async () => {
        const audioData = await textsToSpeechesMutation({
          variables: {
            texts: [text],
            voice: ttsVoice,
            slideId,
            usageContext: MediaUsageContextSchema.Values.aiTextToSpeech,
            speed: 0.8,
            pitch: 0,
          },
        });

        const generated = audioData.data?.textsToSpeeches?.[0];
        if (!generated) {
          throw new Error();
        }

        const nextSlideData = {
          ...baseSlideData,
          sentenceAudio: {
            src: generated.src ?? "",
            filename: generated.filename ?? "",
            id: generated.id ?? "",
          },
        };

        await apolloClient.mutate({
          mutation: UpdateSlideDocument,
          variables: { input: { slideId, slideData: nextSlideData } },
        });
        dispatch(updateSlide({ slideId, slideData: nextSlideData }));
      },
      {
        success: "Sentence audio generated & saved",
        error: "Failed to generate audio",
        loading: "Generating...",
      },
      "sentenceAudioToast"
    )
      .catch(() => {})
      .finally(() => setBusy(null));
  };

  const handleClearSyllableAudios = () => {
    if (!wordSlideData.syllableAudios) return;
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: { ...wordSlideData, syllableAudios: [] },
      })
    );
  };

  const handleToggleVidWithImage = () => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: { ...wordSlideData, isVideoWithImage: !isVideoWithImage },
      })
    );
  };

  const handleToggleRecordOnly = () => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: { ...wordSlideData, recordOnly: !recordOnly },
      })
    );
  };

  const hasAudio = syllableAudios && syllableAudios.length > 0;

  const RemoveButton = ({
    onClick,
    className = "",
  }: {
    onClick: (e: MouseEvent<HTMLButtonElement>) => void;
    className?: string;
  }) => (
    <button
      type="button"
      aria-label="Remove"
      onClick={onClick}
      className={`shrink-0 p-1.5 rounded-md text-muted-foreground enabled:hover:text-white enabled:hover:bg-red-600 enabled:focus-visible:text-white enabled:focus-visible:bg-red-600 transition-colors disabled:opacity-40 ${className}`}
    >
      <HiX className="text-sm" />
    </button>
  );

  const renderAudioStrip = (kind: "word" | "sentence") => {
    const media = kind === "word" ? wordAudio : sentenceAudio;
    const hasClip = Boolean(media?.src);
    const isPlaying = playingIndex === kind;
    const thisBusy = busy === kind;
    const dur = durations[kind] ?? 0;
    const onRegenerate = kind === "word" ? handleRegenerateWord : handleGenerateSentenceAudio;

    const regenerateButton = (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRegenerate();
        }}
        disabled={anyBusy}
        className="flex items-center gap-1 text-[10px] font-semibold text-blue-700 border border-blue-300 enabled:hover:bg-blue-50 px-2 py-1 rounded-full transition-colors whitespace-nowrap shrink-0 disabled:opacity-50"
      >
        <FaMagic className={`text-[9px] ${thisBusy ? "animate-spin" : ""}`} />
        {thisBusy ? "Generating…" : hasClip ? "Regenerate" : "Generate"}
      </button>
    );

    const strip = (
      <div className="flex items-center gap-2 rounded-b-lg border border-t-0 border-border bg-muted/20 px-2 py-1.5">
        {hasClip ? (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAudioPlay(kind);
              }}
              className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-white transition-transform active:scale-95 ${
                isPlaying ? "bg-blue-600" : "bg-blue-500 hover:bg-blue-600"
              }`}
            >
              {isPlaying ? (
                <FaPause className="text-[10px]" />
              ) : (
                <FaPlay className="text-[10px] ml-0.5" />
              )}
            </button>
            <span className="text-[10px] tabular-nums text-muted-foreground shrink-0">
              {formatTime(isPlaying ? currentTime : 0)}
            </span>
            <button
              type="button"
              aria-label="Seek"
              onClick={(e) => {
                e.stopPropagation();
                handleSeek(kind, e);
              }}
              className="flex-1 min-w-0 h-1.5 rounded-full bg-muted overflow-hidden group cursor-pointer"
            >
              <div
                className="h-full bg-blue-500 group-hover:bg-blue-600 pointer-events-none transition-[width] duration-100"
                style={{ width: isPlaying && dur ? `${(currentTime / dur) * 100}%` : "0%" }}
              />
            </button>
            <span className="text-[10px] tabular-nums text-muted-foreground shrink-0">
              {formatTime(dur)}
            </span>
            {regenerateButton}
            <RemoveButton
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveMedia(kind === "word" ? "wordAudio" : "sentenceAudio");
              }}
            />
          </>
        ) : (
          <>
            <FaMusic className="text-muted-foreground text-sm shrink-0" />
            <span className="flex-1 text-xs text-muted-foreground">
              {kind === "word" ? "Drop an audio file, or " : "Press "}
              Generate
            </span>
            {regenerateButton}
          </>
        )}
      </div>
    );

    if (kind === "word") {
      return (
        <DroppableZone
          id={`${slide.id}-audio`}
          data={{ id: slide.id, type: "word-audio" }}
          allowedType={["audio"]}
          className="rounded-b-lg"
          onLocalFileDrop={handleAudioFileDrop}
        >
          {strip}
        </DroppableZone>
      );
    }
    return strip;
  };

  return (
    <EditorContainer>
      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-start gap-2 overflow-auto p-2">
        {/* Header */}
        <div className="flex items-center justify-between px-3 shrink-0 gap-2">
          <h2 className="flex-1 min-w-0 text-sm font-semibold text-primary-900 truncate uppercase tracking-widest">
            {slideName || "Vocabulary"}
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            <label
              className={`flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-full border transition-colors ${
                recordOnly
                  ? "bg-orange-100 border-orange-400 ring-1 ring-orange-300"
                  : "bg-transparent border-orange-200 hover:border-orange-300"
              }`}
            >
              <span
                className={`text-[10px] font-bold uppercase tracking-widest ${
                  recordOnly ? "text-orange-700" : "text-orange-500"
                }`}
              >
                Record only
              </span>
              <button
                type="button"
                onClick={handleToggleRecordOnly}
                className={`relative w-8 h-4 flex items-center rounded-full transition-all duration-300 ease-out ring-1 cursor-pointer ${
                  recordOnly
                    ? "bg-orange-500 ring-orange-600 justify-end"
                    : "bg-orange-200 ring-orange-300"
                }`}
                aria-pressed={!!recordOnly}
              >
                <span className="w-3 h-3 rounded-full bg-white shadow-sm" />
              </button>
            </label>
            <span className="text-[10px] font-medium text-gray-100 bg-primary-900 px-2 py-0.5 rounded-full shrink-0">
              Vocabulary
            </span>
          </div>
        </div>

        <div className="bg-surface rounded-lg p-3 border border-border space-y-3">
          {/* Image */}
          <div className="flex flex-col items-center gap-1.5">
            <DroppableZone
              id={`${slide.id}-image`}
              data={{ id: slide.id, type: "word-image" }}
              allowedType={["image"]}
              className="rounded-lg w-56"
              onLocalFileDrop={handleImageFileDrop}
              onClick={() => setActivePanel("Media")}
            >
              <div className="aspect-square relative w-56 flex items-center justify-center rounded-lg border border-border bg-surface overflow-hidden">
                {isUploadingImage ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-[10px] text-muted-foreground">Uploading…</p>
                  </div>
                ) : image.src ? (
                  <>
                    <img
                      src={transparentBackground}
                      alt={image.src}
                      className="w-full h-full object-cover absolute z-0"
                    />
                    <img
                      src={image.src}
                      alt={image.src}
                      className="w-full h-full object-cover z-30"
                    />
                    <RemoveButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveMedia("image");
                      }}
                      className="absolute top-1.5 right-1.5 z-50 bg-black/30 text-white"
                    />
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-1 px-2 text-center">
                    <AiFillPicture className="text-muted-foreground text-lg" />
                    <p className="text-[10px] font-semibold">Word image</p>
                    <p className="text-[9px] text-muted-foreground">Drag &amp; drop</p>
                  </div>
                )}
              </div>
            </DroppableZone>

            <label className="flex items-center gap-2 cursor-pointer">
              <button
                type="button"
                onClick={handleToggleVidWithImage}
                className={`relative w-8 h-4 flex items-center rounded-full transition-all duration-300 ease-out ring-1 cursor-pointer shrink-0 ${
                  isVideoWithImage
                    ? "bg-blue-500 justify-end ring-blue-600"
                    : "bg-muted ring-border"
                }`}
                aria-pressed={isVideoWithImage}
              >
                <span className="w-3 h-3 rounded-full bg-white shadow-sm" />
              </button>
              <span className="text-[10px] text-muted-foreground">
                Also show on the slide's intro video
              </span>
            </label>
          </div>

          {/* Word + syllables */}
          <fieldset disabled={wordBusy} className="contents">
            <div
              className={`mt-3 pt-3 border-t border-border space-y-3 transition-opacity ${
                wordBusy ? "opacity-50" : ""
              }`}
            >
              <div>
                <label className="text-[10px] font-semibold text-muted-foreground block mb-1">
                  Word
                </label>
                <input
                  type="text"
                  value={word || ""}
                  onChange={(e) =>
                    dispatch(
                      updateSlide({
                        slideId: slide.id,
                        slideData: { ...wordSlideData, word: e.target.value },
                      })
                    )
                  }
                  className="w-full px-3 py-1.5 bg-surface border border-border rounded-t-lg text-sm font-medium focus:border-blue-500 outline-none"
                  placeholder="Enter word"
                />
                {renderAudioStrip("word")}
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setShowSyllables((v) => !v)}
                  className="flex items-center gap-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
                >
                  <FaChevronRight
                    className={`text-[8px] transition-transform ${showSyllables ? "rotate-90" : ""}`}
                  />
                  Syllable breakdown
                  <span className="normal-case tracking-normal font-normal text-muted-foreground/70">
                    — auto-generated
                  </span>
                </button>

                {showSyllables && (
                  <div className="mt-1.5">
                    <div className="flex items-center bg-surface border border-border rounded-t-lg px-2">
                      <input
                        type="text"
                        value={syllable ?? ""}
                        onChange={(e) =>
                          dispatch(
                            updateSlide({
                              slideId: slide.id,
                              slideData: { ...wordSlideData, syllable: e.target.value },
                            })
                          )
                        }
                        className="flex-1 min-w-0 py-1.5 bg-transparent text-sm font-medium outline-none placeholder:text-muted-foreground/50"
                        placeholder="e.g. c-a-t"
                      />
                      {syllable && (
                        <RemoveButton
                          onClick={() =>
                            dispatch(
                              updateSlide({
                                slideId: slide.id,
                                slideData: { ...wordSlideData, syllable: null },
                              })
                            )
                          }
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 rounded-b-lg border border-t-0 border-border bg-muted/20 px-2 py-1.5 min-h-[2.25rem]">
                      <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-x-auto">
                        {syllableAudios.length > 0 ? (
                          syllableAudios.map((syllableItem, index) => (
                            <span key={`syl-${index + 1}`} className="shrink-0">
                              <button
                                type="button"
                                onClick={() => handleSyllablePlay(index)}
                                className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs font-bold text-white transition-colors ${
                                  playingIndex === index
                                    ? "bg-blue-600"
                                    : "bg-blue-500 hover:bg-blue-600"
                                }`}
                              >
                                {syllableItem.syllable}
                                {playingIndex === index ? (
                                  <FaPause className="text-[9px]" />
                                ) : (
                                  <FaPlay className="text-[9px]" />
                                )}
                              </button>
                            </span>
                          ))
                        ) : (
                          <span className="flex items-center gap-2 text-xs text-muted-foreground">
                            <FaMusic className="text-sm shrink-0" />
                            Syllable clips appear here after Generate
                          </span>
                        )}
                      </div>
                      {hasAudio && <RemoveButton onClick={handleClearSyllableAudios} />}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </fieldset>

          {/* Sentence — full width */}
          <fieldset disabled={sentenceBusy} className="contents">
            <div
              className={`mt-3 pt-3 border-t border-border transition-opacity ${
                sentenceBusy ? "opacity-50" : ""
              }`}
            >
              <label className="text-[10px] font-semibold text-muted-foreground block mb-1">
                Sentence (optional)
              </label>
              <input
                type="text"
                value={sentence || ""}
                onChange={(e) =>
                  dispatch(
                    updateSlide({
                      slideId: slide.id,
                      slideData: { ...wordSlideData, sentence: e.target.value },
                    })
                  )
                }
                className={`w-full px-3 py-1.5 bg-surface border border-border text-sm font-medium focus:border-blue-500 outline-none ${
                  sentence?.trim() ? "rounded-t-lg" : "rounded-lg"
                }`}
                placeholder="An example sentence using the word"
              />
              {sentence?.trim() ? (
                renderAudioStrip("sentence")
              ) : (
                <p className="text-[11px] text-muted-foreground italic mt-1 px-1">
                  Add a sentence, then Generate its audio
                </p>
              )}
            </div>
          </fieldset>

          {/* Voice + Generate */}
          <fieldset disabled={anyBusy} className="contents">
            <div className="mt-3 pt-3 border-t border-border space-y-2">
              <label className="text-[10px] font-semibold text-muted-foreground block">
                Voice · used for all generated audio
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={ttsVoice}
                  onChange={(e) => setVoiceOverride(e.target.value)}
                  className="h-7 px-2 bg-surface border border-border rounded text-[11px] font-medium outline-none focus:border-blue-500"
                  aria-label="Voice"
                >
                  {AZURE_VOICES.map((v) => (
                    <option key={v.value} value={v.value}>
                      {v.label} · {v.detail}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handlePreviewVoice}
                  disabled={previewLoading}
                  className="flex items-center gap-1 text-[10px] font-semibold text-blue-700 border border-blue-300 hover:bg-blue-50 px-2 py-0.5 rounded-full transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  <FaPlay className="text-[8px]" />
                  {previewLoading ? "Loading..." : "Preview"}
                </button>
              </div>

              <Button
                type="button"
                onClick={handleGenerateAllClick}
                isLoading={busy === "all"}
                disabled={!(word ?? "").trim()}
                className="w-full"
              >
                <FaMagic className="text-xs" />
                {busy === "all" ? "Generating…" : "Generate audio"}
              </Button>
            </div>
          </fieldset>

          {wordAudio?.src && (
            <audio
              ref={wordAudioRef}
              src={wordAudio.src}
              preload="metadata"
              onLoadedMetadata={(e) => {
                const value = e.currentTarget.duration;
                setDurations((d) => ({ ...d, word: value }));
              }}
            >
              <track kind="captions" srcLang="en" default />
            </audio>
          )}
          {sentenceAudio?.src && (
            <audio
              ref={sentenceAudioRef}
              src={sentenceAudio.src}
              preload="metadata"
              onLoadedMetadata={(e) => {
                const value = e.currentTarget.duration;
                setDurations((d) => ({ ...d, sentence: value }));
              }}
            >
              <track kind="captions" srcLang="en" default />
            </audio>
          )}
          {/* Shared player for syllable chips — see handleSyllablePlay/handleSyllableEnded. `src`
              is set imperatively per tap, so no static `src` prop here. */}
          <audio ref={syllableAudioRef} preload="metadata" onEnded={handleSyllableEnded}>
            <track kind="captions" srcLang="en" default />
          </audio>
        </div>
      </div>
    </EditorContainer>
  );
}
